/* eslint-disable indent */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { AreaChart, Area, YAxis } from 'recharts';
import { connect } from 'unistore/react';
import { useTranslation } from 'react-i18next';

import ErrorBoundary from '@components/ErrorBoundary';
import { Button } from '@components';
import BbNewCoinImg from '../bb-newCoinImg';

import { getCoinProperty, getCoinProps } from '@utils/params';
import { getRatesWithVol, graphData } from '@utils/cred';
import { moneyFormatter } from '@utils/customLib';

let mainCoinList = [];
const graphDataCache = {};

const coinAmmends = {
	skip: ['ZCO', 'SC', 'PHX', 'RPX', 'VEN'],
	switch: {
		ZEBI: { graph: 'ZCO' },
		STORM: { graph: 'STORM', icon: 'STMX' },
		NPXS: { graph: 'NPXS', icon: 'PUNDIX' },
		GNT: { graph: 'GNT', icon: 'GLM' },
		// BCHABC: { graph: 'BCH' },
	},
};
const getPercentageChange = coin =>
	parseFloat(
		((coin.last_traded_price - coin.yes_price) / coin.yes_price) * 100
	).toFixed(2);

const getVolume = coin =>
	coin.volume.volume *
	(coin.inr_price ? coin.inr_price : coin.last_traded_price);

const fixGraphData = data =>
	data.map(e => {
		try {
			e.close = Number(e.close);
			e.high = Number(e.high);
			e.low = Number(e.low);
			e.open = Number(e.open);
			return e;
		} catch (err) {
			return {};
		}
	});

const getGraph = (coinCode, market) => {
	const key = `${coinCode}-${market}`;
	if (graphDataCache[key]) {
		return Promise.resolve(graphDataCache[key]);
	}
	return new Promise((resolve, reject) => {
		const graphPromise = graphData(
			`coin=${coinCode}&market=${market ? 'USDT' : 'INR'}&graphType=6`
		);
		graphPromise
			.then(({ status, data }) => {
				try {
					if (status) {
						graphDataCache[key] = data.reverse();
						return resolve(fixGraphData(data));
					}
					return reject(status);
				} catch (e) {
					return reject(e);
				}
			})
			.catch(e => reject(e));
	});
};

const loadGraph = (__coinCode, __market, setdata) => {
	getGraph(__coinCode, __market)
		.then(dataPoints => {
			setdata(dataPoints);
		})
		.catch(() => {
			// will still render straight line graph incase of data error
			setdata([
				{ price: 0 },
				{ price: 0 },
				{ price: 0 },
				{ price: 0 },
				{ price: 0 },
				{ price: 0 },
			]);
		});
};

const BbCoinTrend = React.memo(({ coinUrl, coinCode, market = 0 }) => {
	const graphObserver = useRef(false);
	const [data, setdata] = useState([]);

	useEffect(() => {
		if ('IntersectionObserver' in window) {
			graphObserver.current = new IntersectionObserver(
				entries => {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							// Stop watching and load the image
							graphObserver.current.unobserve(entry.target);
							loadGraph(coinCode, market, setdata);
						}
					});
				},
				{
					threshold: 1,
				}
			);
			if (graphObserver.current) {
				const $item = document.querySelector(`#token-${coinUrl}`);
				if ($item) {
					graphObserver.current.observe($item);
				}
			}
		} else {
			loadGraph(coinCode, market, setdata);
		}

		return () => {
			graphObserver.current.disconnect();
		};
		// eslint-disable-next-line
	}, [coinUrl, coinCode, market]);

	return (
		<ErrorBoundary>
			<AreaChart width={90} height={40} data={data}>
				<defs>
					<linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
						<stop
							offset="5%"
							stopColor="#A435FF"
							stopOpacity={0.8}
						/>
						<stop
							offset="95%"
							stopColor="#3C0F62"
							stopOpacity={0}
						/>
					</linearGradient>
				</defs>
				<Area
					type="monotone"
					dataKey="close"
					stroke="#C882FF"
					fillOpacity={0.7}
					fill="url(#colorUv)"
				/>
				<YAxis hide type="number" domain={['dataMin', 'dataMax']} />
			</AreaChart>
		</ErrorBoundary>
	);
});

const BbCoinsTable = ({ tradeCoins, __ }) => {
	const [coinList, setCoinList] = useState(null);
	const [tab, setTab] = useState(0);

	const filterCoinList = useCallback(() => {
		if (mainCoinList?.length) {
			const filterList = mainCoinList.sort((coinA, coinB) => {
				if (tab === 1 || tab === 2) {
					const changeA = getPercentageChange(coinA);
					const changeB = getPercentageChange(coinB);
					return tab === 1 ? changeB - changeA : changeA - changeB;
				}
				return getVolume(coinB) - getVolume(coinA);
			});
			setCoinList(filterList.slice(0, 10));
		}
	}, [mainCoinList, tab]);

	const fetchCoinsData = useCallback(async () => {
		try {
			const data = await getRatesWithVol();

			if (typeof data === 'object') {
				mainCoinList = [];

				Object.entries(data).forEach(e => {
					if (
						e[0] === 'USDT' ||
						(tradeCoins.includes(
							e[0].replace(/USDT/g, '-USDT').toLowerCase()
						) &&
							e[1]?.volume?.volume)
					) {
						mainCoinList.push({
							code: e[0],
							...e[1],
						});
					}
				});

				filterCoinList();
			}
		} catch (error) {
			console.log('getRatesWithVol in fetchCoinsData', error);
		}
	}, [tradeCoins]);

	useEffect(() => {
		if (tradeCoins.length > 0) {
			fetchCoinsData();
		}
	}, [tradeCoins]);

	useEffect(() => {
		filterCoinList();
	}, [tab, tradeCoins]);

	const { t } = useTranslation(['common', 'tr-home'], {
		useSuspense: false,
	});

	return (
		<ErrorBoundary>
			<div className="flex items-center justify-center overflow-hidden font-bold">
				<Button
					variant="link"
					className={`mx-5 hover:no-underline focus:outline-none focus:shadow-none ${
						tab === 0 ? 'text-white' : 'text-primary100'
					}`}
					onClick={() => {
						setTab(0);
					}}
				>
					{t('tr-home:miniMarkets.popular')}
				</Button>
				<Button
					variant="link"
					className={`mx-5 hover:no-underline focus:outline-none focus:shadow-none  ${
						tab === 1 ? 'text-white' : 'text-primary100'
					}`}
					onClick={() => {
						setTab(1);
					}}
				>
					{t('Top Gainers')}
				</Button>
				<Button
					variant="link"
					className={`mx-5 hover:no-underline focus:outline-none focus:shadow-none  ${
						tab === 2 ? 'text-white' : 'text-primary100'
					}`}
					onClick={() => {
						setTab(2);
					}}
				>
					{t('Top Losers')}
				</Button>
			</div>

			<table className="c-table c-table--borderLess c-table--fullWidth text-sm md:w-2/3 lg:w-3/4 m-auto text-white my-8 rounded-lg overflow-hidden">
				<thead className="c-table__heading border-0 bg-primary300">
					<tr>
						<th className="c-table__cell py-5">
							{t('common:Name')}
						</th>
						<th className="c-table__cell py-5 px-0">
							{t('common:Price')}
						</th>
						<th className="c-table__cell py-5 text-right xs:text-left">
							{t('common:twentyFourHourChange')}
						</th>
						<th className="c-table__cell py-5 hidden xs:table-cell">
							{t('tr-home:miniMarkets.monthlyChart')}
						</th>
						<th
							width="50"
							className="c-table__cell py-5 hidden xs:table-cell text-center"
						>
							{t('common:Trade')}
						</th>
					</tr>
				</thead>
				<tbody>
					{coinList && coinList.length > 0
						? coinList.map(coin => {
								const { inrDecimals, usdtDecimals } =
									getCoinProps(coin.code);

								const change = getPercentageChange(coin);
								let coinCode = coin.code;
								let isUsdtMarket = false;
								if (coin.code !== 'USDT') {
									const coinCodeParts =
										coin.code.split('USDT');
									[coinCode, isUsdtMarket] = coinCodeParts;
								}
								isUsdtMarket = typeof isUsdtMarket === 'string';

								const graphCoinCode =
									coinAmmends?.switch[coinCode]?.graph ||
									coinCode;

								const deci = isUsdtMarket
									? usdtDecimals
									: inrDecimals;

								coinCode =
									coinAmmends?.switch[coinCode]?.icon ||
									coinCode;
								const coinIcon = getCoinProperty(
									coinCode,
									'coinIcon'
								);
								const coinUrl = `${coinCode}${
									isUsdtMarket ? '-usdt' : ''
								}`;
								return (
									<tr
										className="bg-primary300 border-b-2 border-t-2 border-primary cursor-pointer"
										key={coin.code}
										role="link"
										tabIndex={0}
										onKeyDown={e => {
											if (
												e.code === 'Space' ||
												e.code === 'Enter'
											) {
												e.preventDefault();
												window.location.href = `/trade/#/${coinCode.toLowerCase()}${
													isUsdtMarket ? '-usdt' : ''
												}`;
											}
										}}
										onClick={() => {
											window.location.href = `/trade/#/${coinCode.toLowerCase()}${
												isUsdtMarket ? '-usdt' : ''
											}`;
										}}
									>
										<td className="c-table__cell">
											<div
												className="flex"
												id={`token-${coinUrl}`}
											>
												{coinIcon ? (
													<BbNewCoinImg
														src={coinIcon}
														height="24"
														width="24"
														alt={coinCode}
														className="inline-block align-middle mr-2"
													/>
												) : null}
												<span className="flex flex-col">
													{coinCode}
													{isUsdtMarket
														? ' / USDT'
														: ''}
													<span className="text-purple-500">
														{getCoinProperty(
															coinCode,
															'coinName'
														)}
													</span>
												</span>
											</div>
										</td>
										<td className="c-table__cell px-0">
											{isUsdtMarket ? '$' : '₹'}
											{moneyFormatter(
												coin.last_traded_price,
												deci,
												deci,
												isUsdtMarket ? 'en-US' : 'en-IN'
											)}
										</td>
										<td
											className={`c-table__cell text-right xs:text-left ${
												change < 0
													? 'text-red-600'
													: 'text-green-600'
											}`}
										>
											{change >= 0 ? '+' : ''}
											{change}%
										</td>
										<td className="c-table__cell px-0 hidden xs:table-cell">
											<BbCoinTrend
												coinUrl={coinUrl}
												coinCode={graphCoinCode}
												market={isUsdtMarket ? 1 : 0}
											/>
										</td>
										<td
											className="c-table__cell hidden xs:table-cell text-center"
											width="50"
										>
											<Button
												color="green"
												className="rounded-3xl normal-case font-normal md:lx-10 lg:px-16"
												href={`/trade/#/${coinCode.toLowerCase()}${
													isUsdtMarket ? '-usdt' : ''
												}`}
											>
												Buy
											</Button>
										</td>
									</tr>
								);
						  })
						: null}
					{coinList?.length > 0 && (
						<tr className="bg-primary300 border-t-2 border-primary">
							<td
								className="c-table__cell text-center text-red-600 py-6"
								colSpan="5"
							>
								<a href="/trade/#/markets">
									{t('tr-home:market.seeCoins')}
									<span className="text-white ml-2">⟶</span>
								</a>
							</td>
						</tr>
					)}
					<tr className="bg-primary300 border-t-2 border-primary hidden first:table-row">
						<td className="c-table__cell text-center" colSpan="5">
							{coinList === null
								? 'loading data...'
								: 'no data to show...'}
						</td>
					</tr>
				</tbody>
			</table>
		</ErrorBoundary>
	);
};

export default connect('tradeCoins')(BbCoinsTable);
